import * as THREE from 'three';
import { FikoWebGPU } from './FikoWebGPU';
import { FikoGPUBVH, WGSL_BVH_TRAVERSAL } from './FikoGPUBVH';

/**
 * FikoWebGPURaycast - GPU-Native Raycast System
 *
 * ZERO GPU-CPU STALL ARCHITECTURE
 *
 * Key Innovation: No color buffer, no pixel readback for hover.
 * Ray-BVH intersection runs entirely on GPU compute shader.
 *
 * Features:
 * - Double-buffered async pipeline (1-frame latency, zero stall)
 * - Temporal coherence (reuse result if mouse barely moved)
 * - Stackless BVH traversal (O(log n) complexity)
 * - GPU-resident results (only final object ID transfers to CPU)
 *
 * Performance Targets:
 * - <0.5ms per raycast (vs 5-15ms with color buffer)
 * - 100x faster than brute force for large scenes
 * - Zero frame drops during hover
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface GPURaycastResult {
    hit: boolean;
    objectId: number;
    triangleId: number;
    distance: number;
    hitPoint: THREE.Vector3;
    normal: THREE.Vector3;
    uv: THREE.Vector2;
    object: THREE.Object3D | null;
}

interface AsyncRaycastRequest {
    mouseX: number;
    mouseY: number;
    frameId: number;
    startTime: number;
    resultBuffer: GPUBuffer;
}

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoWebGPURaycast {
    // WebGPU resources
    private webGPU: FikoWebGPU | null = null;
    private device: GPUDevice | null = null;
    private pipeline: GPUComputePipeline | null = null;

    // BVH
    private bvh: FikoGPUBVH;

    // Scene data
    private objectIdToObject: Map<number, THREE.Object3D> = new Map();
    private camera: THREE.Camera | null = null;
    private sceneVersion: number = 0;
    private bvhVersion: number = -1;

    // Double-buffered async pipeline
    private resultBuffers: GPUBuffer[] = [];
    private uniformBuffers: GPUBuffer[] = [];
    private readbackBuffers: GPUBuffer[] = [];
    private currentBuffer: number = 0;
    private frameId: number = 0;

    // Buffer state tracking to prevent "used while mapped" errors
    private bufferMappingInProgress: boolean[] = [false, false];
    private bufferMapped: boolean[] = [false, false];

    // Pending async request
    private pendingRequest: AsyncRaycastRequest | null = null;
    private pendingBufferIndex: number = -1;
    private lastResult: GPURaycastResult | null = null;

    // Temporal coherence
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;
    private coherenceThresholdPx: number = 2;

    // Canvas size
    private canvasWidth: number = 1;
    private canvasHeight: number = 1;

    // Stats
    private stats = {
        raycastCount: 0,
        cacheHits: 0,
        avgRaycastTimeMs: 0,
        totalRaycastTimeMs: 0
    };

    // State
    private initialized: boolean = false;

    constructor() {
        this.bvh = new FikoGPUBVH();
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    async initialize(): Promise<boolean> {
        if (this.initialized) return true;

        try {
            this.webGPU = await FikoWebGPU.initialize();

            if (!this.webGPU || !this.webGPU.available) {
                return false;
            }

            this.device = this.webGPU.gpuDevice;
            if (!this.device) return false;

            // Create compute pipeline
            const shaderModule = this.device.createShaderModule({
                code: WGSL_BVH_TRAVERSAL
            });

            this.pipeline = this.device.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: shaderModule,
                    entryPoint: 'main'
                }
            });

            // Create double-buffered resources
            for (let i = 0; i < 2; i++) {
                // Result buffer (48 bytes for HitResult struct)
                const resultBuffer = this.device.createBuffer({
                    size: 48,
                    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
                });
                this.resultBuffers.push(resultBuffer);

                // Uniform buffer (48 bytes for Uniforms struct)
                const uniformBuffer = this.device.createBuffer({
                    size: 48,
                    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
                });
                this.uniformBuffers.push(uniformBuffer);

                // Readback buffer
                const readbackBuffer = this.device.createBuffer({
                    size: 48,
                    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
                });
                this.readbackBuffers.push(readbackBuffer);
            }

            this.initialized = true;
            return true;
        } catch (e) {
            return false;
        }
    }

    // ============================================================================
    // SCENE MANAGEMENT
    // ============================================================================

    /**
     * Build BVH from scene objects
     */
    buildFromScene(objects: THREE.Object3D[]): void {
        // Update object ID mapping
        this.objectIdToObject.clear();
        objects.forEach((obj, idx) => {
            this.objectIdToObject.set(idx, obj);
        });

        // Build BVH
        this.bvh.build(objects);

        // Upload to GPU
        this.bvh.uploadToGPU();

        this.sceneVersion++;
        this.bvhVersion = this.sceneVersion;
    }

    /**
     * Mark object as changed (triggers lazy rebuild)
     */
    markObjectDirty(objectId: number): void {
        this.bvh.markDirty(objectId);
    }

    /**
     * Update camera reference
     */
    setCamera(camera: THREE.Camera): void {
        this.camera = camera;
    }

    /**
     * Update canvas size for ray calculation
     */
    setCanvasSize(width: number, height: number): void {
        this.canvasWidth = width;
        this.canvasHeight = height;
    }

    // ============================================================================
    // RAYCAST API
    // ============================================================================

    /**
     * Submit raycast request (non-blocking)
     * Returns last known result immediately while new request processes
     *
     * @param mouseX Mouse X in pixels
     * @param mouseY Mouse Y in pixels
     * @returns Last known result (may be from previous frame)
     */
    submitRaycast(mouseX: number, mouseY: number): GPURaycastResult | null {
        if (!this.initialized || !this.camera || !this.bvh.isReady()) {
            return null;
        }

        // Temporal coherence check
        const dx = mouseX - this.lastMouseX;
        const dy = mouseY - this.lastMouseY;

        if (Math.abs(dx) < this.coherenceThresholdPx &&
            Math.abs(dy) < this.coherenceThresholdPx &&
            this.lastResult) {
            this.stats.cacheHits++;
            return this.lastResult;
        }

        // Update mouse position
        this.lastMouseX = mouseX;
        this.lastMouseY = mouseY;

        // Poll previous request if pending
        this.pollPendingResult();

        // Submit new request
        this.dispatchRaycast(mouseX, mouseY);

        // Return last known result (1-frame latency)
        return this.lastResult;
    }

    /**
     * Blocking raycast (wait for GPU result)
     * Use only when you need immediate result
     */
    async raycastSync(mouseX: number, mouseY: number): Promise<GPURaycastResult | null> {
        if (!this.initialized || !this.camera || !this.bvh.isReady()) {
            return null;
        }

        const startTime = performance.now();

        // Submit and wait
        this.dispatchRaycast(mouseX, mouseY);
        await this.waitForResult();

        const elapsed = performance.now() - startTime;
        this.stats.raycastCount++;
        this.stats.totalRaycastTimeMs += elapsed;
        this.stats.avgRaycastTimeMs = this.stats.totalRaycastTimeMs / this.stats.raycastCount;

        return this.lastResult;
    }

    // ============================================================================
    // INTERNAL METHODS
    // ============================================================================

    /**
     * Dispatch raycast compute shader
     */
    private dispatchRaycast(mouseX: number, mouseY: number): void {
        if (!this.device || !this.pipeline) return;

        // Find an available buffer (not currently being mapped)
        let bufferIdx = this.currentBuffer;

        // If current buffer is being mapped, try the other one
        if (this.bufferMappingInProgress[bufferIdx] || this.bufferMapped[bufferIdx]) {
            const otherIdx = (bufferIdx + 1) % 2;
            if (!this.bufferMappingInProgress[otherIdx] && !this.bufferMapped[otherIdx]) {
                bufferIdx = otherIdx;
            } else {
                // Both buffers busy, skip this frame
                return;
            }
        }

        this.currentBuffer = (bufferIdx + 1) % 2;
        this.frameId++;
        this.pendingBufferIndex = bufferIdx;

        // Calculate ray from mouse position
        const ray = this.calculateRay(mouseX, mouseY);
        if (!ray) return;

        // Update uniforms
        const uniforms = new Float32Array([
            ray.origin.x, ray.origin.y, ray.origin.z,
            ray.direction.x, ray.direction.y, ray.direction.z,
            0.001,  // tMin
            10000,  // tMax
            0, 0, 0, 0  // nodeCount, triangleCount, padding (set as u32)
        ]);

        // Set node and triangle counts
        const bvhStats = this.bvh.getStats();
        const view = new DataView(uniforms.buffer);
        view.setUint32(8 * 4, bvhStats.nodeCount, true);
        view.setUint32(9 * 4, bvhStats.triangleCount, true);

        this.device.queue.writeBuffer(this.uniformBuffers[bufferIdx], 0, uniforms);

        // Create bind group
        const buffers = this.bvh.getBuffers();
        if (!buffers.nodeBuffer || !buffers.triangleBuffer) return;

        const bindGroup = this.device.createBindGroup({
            layout: this.pipeline.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: buffers.nodeBuffer } },
                { binding: 1, resource: { buffer: buffers.triangleBuffer } },
                { binding: 2, resource: { buffer: this.resultBuffers[bufferIdx] } },
                { binding: 3, resource: { buffer: this.uniformBuffers[bufferIdx] } }
            ]
        });

        // Dispatch
        const commandEncoder = this.device.createCommandEncoder();

        const computePass = commandEncoder.beginComputePass();
        computePass.setPipeline(this.pipeline);
        computePass.setBindGroup(0, bindGroup);
        computePass.dispatchWorkgroups(1); // Single workgroup for BVH traversal
        computePass.end();

        // Copy result to readback buffer
        commandEncoder.copyBufferToBuffer(
            this.resultBuffers[bufferIdx], 0,
            this.readbackBuffers[bufferIdx], 0,
            48
        );

        this.device.queue.submit([commandEncoder.finish()]);

        // Store pending request
        this.pendingRequest = {
            mouseX,
            mouseY,
            frameId: this.frameId,
            startTime: performance.now(),
            resultBuffer: this.readbackBuffers[bufferIdx]
        };
    }

    /**
     * Poll for pending raycast result
     */
    private pollPendingResult(): void {
        if (!this.pendingRequest || this.pendingBufferIndex < 0) return;

        const bufferIdx = this.pendingBufferIndex;
        const buffer = this.pendingRequest.resultBuffer;

        // Already mapping or mapped - don't start another map
        if (this.bufferMappingInProgress[bufferIdx]) {
            return;
        }

        // If already mapped, process it
        if (this.bufferMapped[bufferIdx]) {
            this.processResult(buffer, bufferIdx);
            return;
        }

        // Start async mapping
        this.bufferMappingInProgress[bufferIdx] = true;

        buffer.mapAsync(GPUMapMode.READ).then(() => {
            this.bufferMappingInProgress[bufferIdx] = false;
            this.bufferMapped[bufferIdx] = true;
            this.processResult(buffer, bufferIdx);
        }).catch(() => {
            // Mapping failed, reset state
            this.bufferMappingInProgress[bufferIdx] = false;
        });
    }

    /**
     * Wait for result (blocking)
     */
    private async waitForResult(): Promise<void> {
        if (!this.pendingRequest || this.pendingBufferIndex < 0) return;

        const bufferIdx = this.pendingBufferIndex;
        const buffer = this.pendingRequest.resultBuffer;

        // Mark as mapped (blocking call)
        this.bufferMapped[bufferIdx] = true;
        await buffer.mapAsync(GPUMapMode.READ);
        this.processResult(buffer, bufferIdx);
    }

    /**
     * Process raycast result from GPU buffer
     */
    private processResult(buffer: GPUBuffer, bufferIdx: number): void {
        const mappedRange = buffer.getMappedRange();
        const data = new Float32Array(mappedRange.slice(0));
        buffer.unmap();

        // Reset buffer state after unmap
        this.bufferMapped[bufferIdx] = false;

        // Parse HitResult struct
        const view = new DataView(data.buffer);
        const hit = view.getUint32(0, true) > 0;
        const objectId = view.getUint32(4, true);
        const triangleId = view.getUint32(8, true);
        const distance = view.getFloat32(12, true);
        const hitPointX = view.getFloat32(16, true);
        const hitPointY = view.getFloat32(20, true);
        const hitPointZ = view.getFloat32(24, true);
        const normalX = view.getFloat32(28, true);
        const normalY = view.getFloat32(32, true);
        const normalZ = view.getFloat32(36, true);
        const u = view.getFloat32(40, true);
        const v = view.getFloat32(44, true);

        // Create result
        this.lastResult = {
            hit,
            objectId,
            triangleId,
            distance,
            hitPoint: new THREE.Vector3(hitPointX, hitPointY, hitPointZ),
            normal: new THREE.Vector3(normalX, normalY, normalZ),
            uv: new THREE.Vector2(u, v),
            object: hit ? (this.objectIdToObject.get(objectId) || null) : null
        };

        this.pendingRequest = null;
    }

    /**
     * Calculate ray from mouse position
     */
    private calculateRay(mouseX: number, mouseY: number): THREE.Ray | null {
        if (!this.camera) return null;

        // Convert to NDC (-1 to 1)
        const ndcX = (mouseX / this.canvasWidth) * 2 - 1;
        const ndcY = -(mouseY / this.canvasHeight) * 2 + 1;

        // Unproject to world space
        const near = new THREE.Vector3(ndcX, ndcY, -1).unproject(this.camera);
        const far = new THREE.Vector3(ndcX, ndcY, 1).unproject(this.camera);

        const direction = new THREE.Vector3().subVectors(far, near).normalize();

        return new THREE.Ray(near, direction);
    }

    // ============================================================================
    // UTILITY
    // ============================================================================

    /**
     * Check if raycast system is available
     */
    isAvailable(): boolean {
        return this.initialized && this.bvh.isReady();
    }

    /**
     * Get performance statistics
     */
    getStats() {
        return {
            ...this.stats,
            bvhStats: this.bvh.getStats(),
            cacheHitRate: this.stats.raycastCount > 0
                ? this.stats.cacheHits / (this.stats.raycastCount + this.stats.cacheHits)
                : 0
        };
    }

    /**
     * Convert GPU result to THREE.Intersection format
     */
    toThreeIntersection(result: GPURaycastResult): THREE.Intersection | null {
        if (!result.hit || !result.object) {
            return null;
        }

        return {
            object: result.object,
            distance: result.distance,
            point: result.hitPoint.clone(),
            face: {
                a: 0,
                b: 0,
                c: 0,
                normal: result.normal.clone(),
                materialIndex: 0
            },
            faceIndex: result.triangleId,
            uv: result.uv.clone()
        };
    }

    /**
     * Dispose all GPU resources
     */
    dispose(): void {
        for (const buffer of this.resultBuffers) {
            buffer.destroy();
        }
        for (const buffer of this.uniformBuffers) {
            buffer.destroy();
        }
        for (const buffer of this.readbackBuffers) {
            buffer.destroy();
        }

        this.resultBuffers = [];
        this.uniformBuffers = [];
        this.readbackBuffers = [];

        this.bvh.dispose();

        this.initialized = false;
    }
}

// ============================================================================
// SINGLETON ACCESSOR
// ============================================================================

let instance: FikoWebGPURaycast | null = null;

export async function getWebGPURaycastInstance(): Promise<FikoWebGPURaycast | null> {
    if (!instance) {
        instance = new FikoWebGPURaycast();
        const success = await instance.initialize();
        if (!success) {
            instance = null;
        }
    }
    return instance;
}
